<script setup lang="ts">
  // The one-time nudge to "Plan with AI". It appears on the event's home page or agenda when the user is able to use it
  // (a ticket for the event, onboarding done, an agenda with sessions) and has not seen it for this event yet:
  // right after they finish onboarding, or the next time they open an event they were already onboarded in.
  // Closing it in any way, or starting the plan, counts as seen, and it never shows again for that event.
  //
  // "Seen" is kept in this browser (localStorage, one key per event), not on the server: it is a hint, not a setting,
  // and a second device showing it once more costs nothing. Where storage is blocked, it is remembered for the session.
  const props = defineProps<{
    // The onboarding form is open: wait until it is done, so this follows it instead of covering it.
    blocked: boolean,
  }>();

  const route = useRoute();
  const router = useRouter();
  const {eventData, eventHasEnded} = storeToRefs(useEventsStore());
  const {userLoggedIn, hasEventTicket, requireEmailVerification} = useWadAuth();
  const aiPlanOpen = useState<boolean>('wad4-ai-plan-open', () => false);

  // A user who has not decided about push notifications gets that prompt a few seconds after load, and a brand-new
  // user is exactly who sees this popup first. Two modals on top of each other is one too many: wait for that one.
  const {notificationPermission, isNotificationSupported} = useFirebaseNotification();
  const {modalEnableNotificationsClosed} = storeToRefs(useNotificationsStore());
  const pushPromptOpen = computed(() => isNotificationSupported.value && !modalEnableNotificationsClosed.value && notificationPermission.value !== 'granted');

  const SHOW_ON_ROUTES = ['events-eventId', 'events-eventId-agenda'];
  // Let the page settle first: a popup in the first frame reads as part of the loading.
  const SHOW_DELAY_MS = 1200;

  const storageKey = (eventId: number): string => `wad4-ai-plan-intro-seen-${eventId}`;
  const seenThisSession = new Set<number>();
  const hasSeen = (eventId: number): boolean => {
    if(seenThisSession.has(eventId)) {
      return true;
    }
    try {
      return localStorage.getItem(storageKey(eventId)) === '1';
    } catch {
      return false;
    }
  };
  const markSeen = (eventId: number): void => {
    seenThisSession.add(eventId);
    try {
      localStorage.setItem(storageKey(eventId), '1');
    } catch {
      // Storage blocked: seenThisSession is all there is.
    }
  };
  // localStorage is not reactive: bumped when something is marked seen, so `eligible` is evaluated again.
  const seenTick = ref(0);

  const routeEventId = computed(() => route.params.eventId ? Number(route.params.eventId) : null);
  const eligible = computed(() => {
    void seenTick.value;
    const eventId = routeEventId.value;
    if(!eventId || props.blocked || pushPromptOpen.value || !userLoggedIn.value || requireEmailVerification.value || !hasEventTicket(eventId)) {
      return false;
    }
    return SHOW_ON_ROUTES.includes(String(route.name))
        && eventData.value?.id === eventId
        && eventData.value?.event_type === wadConstants.EVENT_TYPE.WORLD_CONGRESS
        && (eventData.value?.sessions?.length ?? 0) > 0
        && !eventData.value?.program_preview
        && !eventHasEnded.value
        && !aiPlanOpen.value
        && !hasSeen(eventId);
  });

  const open = ref(false);
  const shownFor = ref<number | null>(null);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clearTimer = () => {
    if(timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  watch(eligible, (isEligible) => {
    clearTimer();
    if(isEligible) {
      timer = setTimeout(() => {
        // Still true after the delay: the user did not leave the page or start the plan in the meantime.
        if(eligible.value) {
          shownFor.value = routeEventId.value;
          open.value = true;
        }
      }, SHOW_DELAY_MS);
    }
  }, {immediate: true});
  onBeforeUnmount(clearTimer);

  // Every way of closing it (the X, tapping outside, "Not now", "Plan my event") ends up here.
  watch(open, (isOpen) => {
    if(!isOpen && shownFor.value) {
      markSeen(shownFor.value);
      seenTick.value++;
    }
  });

  const start = async () => {
    const eventId = shownFor.value;
    open.value = false;
    if(eventId && route.name !== 'events-eventId-agenda') {
      await router.push({name: 'events-eventId-agenda', params: {eventId}});
    }
    aiPlanOpen.value = true;
  };
</script>

<template>
  <common-wad-modal v-model="open" :show-close-icon="true" title="Plan your event with AI">
    <template #content>
      <div class="wad4-ai-intro">
        <!-- Soft drifting gradient with a fine grain, the look of a generative product page, in pure CSS (no video, no image). -->
        <div class="wad4-ai-intro__hero">
          <!-- The feature in one picture: a mini agenda where a soft highlight (the agent) scans the sessions, the ones it
               picks light up as bookmarks, and a star twinkles. Looping CSS animation, still when motion is reduced. -->
          <svg class="wad4-ai-art" width="168" height="124" viewBox="0 0 168 124" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="wad4-ai-art-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#3746f4"/>
                <stop offset="0.55" stop-color="#8a3ffc"/>
                <stop offset="1" stop-color="#ff2f6b"/>
              </linearGradient>
              <clipPath id="wad4-ai-art-card"><rect x="22" y="16" width="124" height="96" rx="18"/></clipPath>
            </defs>
            <g class="wad4-ai-art__card">
              <rect x="22" y="16" width="124" height="96" rx="18" fill="#ffffff"/>
              <g clip-path="url(#wad4-ai-art-card)">
                <rect class="wad4-ai-art__scan" x="22" y="42" width="124" height="24" fill="url(#wad4-ai-art-grad)"/>
              </g>
              <rect x="38" y="30" width="44" height="7" rx="3.5" fill="#d9dcec"/>
              <rect x="116" y="30" width="16" height="7" rx="3.5" fill="#e6e8f3"/>
              <!-- three sessions: a title bar and a bookmark each -->
              <g>
                <rect x="38" y="50" width="58" height="7" rx="3.5" fill="#d9dcec"/>
                <path d="M114 46h12v16l-6-4-6 4z" stroke="#b6bbd2" stroke-width="2" stroke-linejoin="round"/>
                <path class="wad4-ai-art__pick wad4-ai-art__pick--one" d="M114 46h12v16l-6-4-6 4z" fill="url(#wad4-ai-art-grad)"/>
              </g>
              <g>
                <rect x="38" y="70" width="48" height="7" rx="3.5" fill="#d9dcec"/>
                <path d="M114 66h12v16l-6-4-6 4z" stroke="#b6bbd2" stroke-width="2" stroke-linejoin="round"/>
              </g>
              <g>
                <rect x="38" y="90" width="64" height="7" rx="3.5" fill="#d9dcec"/>
                <path d="M114 86h12v16l-6-4-6 4z" stroke="#b6bbd2" stroke-width="2" stroke-linejoin="round"/>
                <path class="wad4-ai-art__pick wad4-ai-art__pick--two" d="M114 86h12v16l-6-4-6 4z" fill="url(#wad4-ai-art-grad)"/>
              </g>
            </g>
            <!-- the stars -->
            <path class="wad4-ai-art__star wad4-ai-art__star--big" d="M144 4l3.4 9.2L156.6 16.6l-9.2 3.4L144 29.2l-3.4-9.2-9.2-3.4 9.2-3.4z" fill="url(#wad4-ai-art-grad)"/>
            <path class="wad4-ai-art__star wad4-ai-art__star--small" d="M20 92l2 5.4 5.4 2-5.4 2-2 5.4-2-5.4-5.4-2 5.4-2z" fill="url(#wad4-ai-art-grad)"/>
          </svg>
          <p class="wad4-ai-intro__lead">Let your agent pick the sessions that fit you</p>
          <p class="wad4-ai-intro__text">
            Your assistant reads the agenda and suggests the sessions to attend. You review the list and add it to your
            agenda in one tap. Your conversation stays private: we only get the sessions directly from you.
          </p>
        </div>
      </div>
    </template>
    <template #controls>
      <div class="wad4-ai-intro__buttons">
        <button type="button" class="wad4-button-primary wad4-ai-intro__button" @click="start">Plan my event</button>
        <button type="button" class="wad4-button-secondary wad4-ai-intro__button" @click="open = false">Not now</button>
      </div>
    </template>
  </common-wad-modal>
</template>

<style lang="scss">
.wad4-ai-intro {
  padding: $wad4-spacing-lg 0;

  // The gradient is made of three blurred colour fields that drift slowly over a base colour, with a grain on top, in
  // the WAD brand colours: the brand blue, the WAD red and a violet between them. Light theme: soft tints of them over a
  // warm cream, like a sunrise. Dark theme: the saturated colours glowing out of an ink-blue base. The text colours are
  // fixed per theme (not the app tokens) because they sit on these colours, not on the page background.
  &__hero {
    --ai-base: #fff2ec;
    --ai-a: #ff9db3;
    --ai-b: #b4c4ff;
    --ai-c: #ffd0a6;
    --ai-ink: #15112e;
    --ai-ink-soft: rgba(21, 17, 46, 0.76);
    position: relative;
    overflow: hidden;
    isolation: isolate;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $wad4-spacing-md;
    padding: $wad4-spacing-3xl $wad4-spacing-xl;
    border-radius: $wad4-border-radius-lg;
    background: var(--ai-base);
    text-align: center;

    &::before {
      content: '';
      position: absolute;
      inset: -30%;
      z-index: -2;
      background:
        radial-gradient(40% 36% at 22% 28%, var(--ai-a) 0%, transparent 70%),
        radial-gradient(46% 40% at 80% 22%, var(--ai-b) 0%, transparent 72%),
        radial-gradient(52% 44% at 58% 86%, var(--ai-c) 0%, transparent 72%);
      filter: blur(28px);
      animation: wad4-ai-intro-drift 16s ease-in-out infinite alternate;
    }

    // Fine grain, like film: noise from an inline SVG filter, blended over the colour fields.
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      mix-blend-mode: soft-light;
      opacity: 0.55;
      pointer-events: none;
    }
  }

  // The illustration. The card is white in both themes (it sits on the gradient, not on the page background).
  .wad4-ai-art {
    flex-shrink: 0;
    overflow: visible;
    filter: drop-shadow(0 10px 22px rgba(15, 16, 48, 0.22));

    // Every animated shape scales and fades around its own centre.
    &__pick,
    &__star {
      transform-box: fill-box;
      transform-origin: center;
    }

    // One loop of 7 s: the highlight looks at the first session, the agent picks it, skips the second, looks at the
    // third and picks it; then everything fades and starts over.
    &__scan {
      opacity: 0;
      animation: wad4-ai-art-scan 7s ease-in-out infinite;
    }

    &__pick {
      opacity: 0;
      animation: wad4-ai-art-pick-one 7s ease-in-out infinite;

      &--two {
        animation-name: wad4-ai-art-pick-two;
      }
    }

    &__star {
      animation: wad4-ai-art-twinkle 3.2s ease-in-out infinite;

      &--small {
        animation-delay: -1.6s;
      }
    }
  }

  &__lead {
    margin: $wad4-spacing-sm 0 0;
    color: var(--ai-ink);
    @include title-display-mixin('xs');
    // No one-word last line ("you.").
    text-wrap: balance;
  }

  &__text {
    margin: 0;
    max-width: 34ch;
    color: var(--ai-ink-soft);
    @include text-size-mixin('md');
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-md;
  }

  &__button {
    width: 100%;
    text-align: center;
    border-radius: $wad4-border-radius-full;
    box-shadow: none;
  }
}

:root[data-theme='dark'] .wad4-ai-intro__hero {
  --ai-base: #0b0f24;
  --ai-a: #3746f4;
  --ai-b: #c4124b;
  --ai-c: #6c2dc7;
  --ai-ink: #ffffff;
  --ai-ink-soft: rgba(255, 255, 255, 0.78);
}

@keyframes wad4-ai-art-scan {
  0%        { opacity: 0; transform: translateY(0); }
  5%        { opacity: 0.2; transform: translateY(0); }
  22%       { opacity: 0.2; transform: translateY(0); }
  34%       { opacity: 0.2; transform: translateY(20px); }
  48%       { opacity: 0.2; transform: translateY(20px); }
  62%       { opacity: 0.2; transform: translateY(40px); }
  76%       { opacity: 0.2; transform: translateY(40px); }
  86%, 100% { opacity: 0; transform: translateY(40px); }
}

@keyframes wad4-ai-art-pick-one {
  0%, 17%   { opacity: 0; transform: scale(0.5); }
  23%       { opacity: 1; transform: scale(1.3); }
  29%, 88%  { opacity: 1; transform: scale(1); }
  96%, 100% { opacity: 0; transform: scale(1); }
}

@keyframes wad4-ai-art-pick-two {
  0%, 65%   { opacity: 0; transform: scale(0.5); }
  71%       { opacity: 1; transform: scale(1.3); }
  77%, 88%  { opacity: 1; transform: scale(1); }
  96%, 100% { opacity: 0; transform: scale(1); }
}

@keyframes wad4-ai-art-twinkle {
  0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
  50%      { opacity: 0.55; transform: scale(0.72) rotate(18deg); }
}

@keyframes wad4-ai-intro-drift {
  from { transform: translate3d(-4%, -3%, 0) rotate(0deg) scale(1); }
  to   { transform: translate3d(5%, 4%, 0) rotate(8deg) scale(1.08); }
}

@media (prefers-reduced-motion: reduce) {
  .wad4-ai-intro__hero::before {
    animation: none;
  }

  // Still picture: the two picks are shown, the scan and the twinkle are off.
  .wad4-ai-intro .wad4-ai-art {
    &__scan,
    &__star {
      animation: none;
    }

    &__pick {
      animation: none;
      opacity: 1;
    }
  }
}
</style>
